import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '@/hooks/useAppDispatch';
import { toggleSelection, selectAll, importConsignment } from '@/store/slices/consignmentSlice';
import { Consignment } from '@/data/mockData';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Table, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { WideDialogContent } from '@/components/ui/custom/WideDialogContent';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { SlidersHorizontal, Maximize2, ArrowUp, ArrowDown } from 'lucide-react';
import { ConvoyGroup, SortableColumnRow } from '@/shared/ui';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy
} from '@dnd-kit/sortable';

interface ConsignmentTableProps {
  title: string;
  data: Consignment[];
  tableId: string;
}

interface ColumnConfig {
  id: string;
  label: string;
  description: string;
  visible: boolean;
}

const ConsignmentTable: React.FC<ConsignmentTableProps> = ({ title, data, tableId }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { selectedIds } = useAppSelector((state) => state.consignment);
  const [isCustomizeOpen, setIsCustomizeOpen] = React.useState(false);
  const [sortConfig, setSortConfig] = React.useState<{ key: string; direction: 'asc' | 'desc' } | null>(null);

  const [columns, setColumns] = React.useState<ColumnConfig[]>(() => {
    const saved = localStorage.getItem('consignment_table_columns');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        console.error('Failed to parse saved columns', e);
      }
    }
    return [
      { id: 'shipment', label: 'table.shipment', description: 'columns.shipmentDesc', visible: true },
      { id: 'vehicle', label: 'table.vehicle', description: 'columns.vehicleDesc', visible: true },
      { id: 'arrivingTo', label: 'table.arrivingTo', description: 'columns.arrivingToDesc', visible: true },
      {
        id: 'consignmentNumber',
        label: 'table.consignmentNumber',
        description: 'columns.consignmentNumberDesc',
        visible: true
      },
      { id: 'dispatched', label: 'table.dispatched', description: 'columns.dispatchedDesc', visible: true },
      { id: 'waybill', label: 'table.waybill', description: 'columns.waybillDesc', visible: true },
      { id: 'sender', label: 'table.sender', description: 'columns.senderDesc', visible: true }
    ];
  });

  // Temporary state for the modal
  const [tempColumns, setTempColumns] = React.useState<ColumnConfig[]>(columns);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates
    })
  );

  const handleOpenCustomize = () => {
    setTempColumns(columns);
    setIsCustomizeOpen(true);
  };

  const handleConfirmCustomize = () => {
    setColumns(tempColumns);
    localStorage.setItem('consignment_table_columns', JSON.stringify(tempColumns));
    setIsCustomizeOpen(false);
  };

  const handleToggleColumn = (id: string) => {
    setTempColumns((prev) => prev.map((col) => (col.id === id ? { ...col, visible: !col.visible } : col)));
  };

  const handleSort = (key: string) => {
    setSortConfig((current) => {
      if (current?.key === key) {
        return current.direction === 'asc' ? { key, direction: 'desc' } : null;
      }
      return { key, direction: 'asc' };
    });
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      setTempColumns((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over?.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  const allSelected = data.length > 0 && data.every((item) => selectedIds.includes(item.id));
  const someSelected = data.some((item) => selectedIds.includes(item.id)) && !allSelected;

  // Group data by shipment
  const groupedData = useMemo(() => {
    const sortedData = [...data];
    if (sortConfig) {
      sortedData.sort((a, b) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const aValue = (a as any)[sortConfig.key];
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const bValue = (b as any)[sortConfig.key];

        if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }

    const groups: { [key: string]: Consignment[] } = {};
    sortedData.forEach((item) => {
      if (!groups[item.shipment]) {
        groups[item.shipment] = [];
      }
      groups[item.shipment].push(item);
    });
    return groups;
  }, [data, sortConfig]);

  const handleSelectAll = () => {
    if (allSelected) {
      const tableIds = data.map((item) => item.id);
      const newSelection = selectedIds.filter((id) => !tableIds.includes(id));
      dispatch(selectAll(newSelection));
    } else {
      const tableIds = data.map((item) => item.id);
      const newSelection = [...new Set([...selectedIds, ...tableIds])];
      dispatch(selectAll(newSelection));
    }
  };

  const handleToggleSelection = (id: string) => {
    dispatch(toggleSelection(id));
  };

  const handleImport = (id: string) => {
    dispatch(importConsignment(id));
  };

  const renderTableContent = () => (
    <div className="px-6 pb-4 h-full">
      <Table className="w-full table-fixed">
        <TableHeader>
          <TableRow>
            <TableHead className="w-[5%]">{/* Group Header Placeholder */}</TableHead>
            {columns
              .filter((col) => col.id !== 'shipment' && col.visible)
              .map((col) => (
                <TableHead
                  key={col.id}
                  className={
                    col.id === 'vehicle' || col.id === 'sender'
                      ? 'w-[8%] cursor-pointer hover:bg-muted/50 transition-colors'
                      : col.id === 'arrivingTo'
                        ? 'w-[32%] cursor-pointer hover:bg-muted/50 transition-colors'
                        : col.id === 'consignmentNumber'
                          ? 'w-[15%] cursor-pointer hover:bg-muted/50 transition-colors'
                          : 'w-[10%] cursor-pointer hover:bg-muted/50 transition-colors'
                  }
                  onClick={() => handleSort(col.id)}
                  data-testid={`column-header-${col.id}`}>
                  <div className="flex items-center gap-1">
                    {t(col.label)}
                    {sortConfig?.key === col.id &&
                      (sortConfig.direction === 'asc' ? (
                        <ArrowUp className="w-3 h-3" />
                      ) : (
                        <ArrowDown className="w-3 h-3" />
                      ))}
                  </div>
                </TableHead>
              ))}
            <TableHead className="w-[8%]">{t('common.actions')}</TableHead>
            <TableHead className="w-[4%]">
              <Checkbox
                checked={allSelected}
                ref={(el) => {
                  if (el) {
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    (el as any).indeterminate = someSelected;
                  }
                }}
                onCheckedChange={handleSelectAll}
                aria-label={t('common.selectAll')}
              />
            </TableHead>
          </TableRow>
        </TableHeader>
      </Table>

      <div className="mt-2 space-y-4">
        {Object.entries(groupedData).map(([shipment, items]) => (
          <ConvoyGroup
            key={shipment}
            shipment={shipment}
            items={items}
            selectedIds={selectedIds}
            onToggleSelection={handleToggleSelection}
            onImport={handleImport}
            tableId={tableId}
            columns={columns.filter((c) => c.id !== 'shipment')}
          />
        ))}
      </div>
    </div>
  );

  return (
    <section className="flex w-full flex-col border border-border shadow-sm rounded-lg bg-card mb-4">
      <div className="flex justify-between items-center p-6 pb-4">
        <h3 className="text-foreground text-xl font-semibold">{title}</h3>
        <div className="flex items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-9 w-9" data-testid="table-options-button">
                <SlidersHorizontal className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuItem onClick={handleOpenCustomize}>{t('common.customizeColumns')}</DropdownMenuItem>
              <DropdownMenuItem>{t('common.exportTable')}</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <Dialog open={isCustomizeOpen} onOpenChange={setIsCustomizeOpen}>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>{t('common.customizeColumns')}</DialogTitle>
              </DialogHeader>
              <div className="py-4">
                <p className="text-sm text-muted-foreground mb-4">{t('common.selectColumns')}</p>
                <div className="space-y-4">
                  <div className="grid grid-cols-[1fr,2fr,auto] gap-4 px-2 py-2 font-medium text-sm text-muted-foreground border-b">
                    <div>{t('common.columnName')}</div>
                    <div>{t('common.description')}</div>
                    <div>{t('common.display')}</div>
                  </div>
                  <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                    <SortableContext items={tempColumns.map((c) => c.id)} strategy={verticalListSortingStrategy}>
                      {tempColumns.map((col) => (
                        <SortableColumnRow key={col.id} column={col} onToggle={handleToggleColumn} t={t} />
                      ))}
                    </SortableContext>
                  </DndContext>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setIsCustomizeOpen(false)}>
                  {t('common.cancel')}
                </Button>
                <Button onClick={handleConfirmCustomize}>{t('common.confirm')}</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          <Dialog>
            <DialogTrigger asChild>
              <Button variant="outline" size="icon" className="h-9 w-9">
                <Maximize2 className="w-4 h-4" />
              </Button>
            </DialogTrigger>
            <WideDialogContent className="h-[90vh] flex flex-col p-0 gap-0">
              <DialogHeader className="p-6 pb-2 border-b">
                <DialogTitle>{title}</DialogTitle>
              </DialogHeader>
              <div className="flex-1 overflow-y-auto py-4">{renderTableContent()}</div>
            </WideDialogContent>
          </Dialog>
        </div>
      </div>

      {renderTableContent()}
    </section>
  );
};

export default ConsignmentTable;
